import React, {memo, useCallback, useMemo} from 'react';

import AddIcon from '@mui/icons-material/Add';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {FdicIcon} from '@/app/main/splits/components/FdicIcon';
import {
  AppButton,
  AppButtonVariant,
  LetterAvatar,
} from '@/app/shared-components';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import {InviterAvatar} from '@/app/shared-components/InviterAvatar/InviterAvatar';
import {COLORS} from '@/styles/colors';

import {NEW_PARTICIPANT_ID} from '../../models/constants';
import {IGroupParticipant} from '../../models/interfaces';

interface GroupsSelectMemberItemProps {
  participant: IGroupParticipant;
  isSelected: boolean;
  onSelect: (participant: IGroupParticipant) => void;
  isNewParticipant?: boolean;
}

const GroupsSelectMemberItem = memo(
  ({
    participant,
    isSelected,
    isNewParticipant,
    onSelect,
  }: GroupsSelectMemberItemProps) => {
    const handleClick = useCallback(() => {
      onSelect(participant);
    }, [onSelect, participant]);

    return (
      <Box
        component="button"
        className={`splits-select-member-item ${isSelected ? 'selected' : ''}`}
        onClick={handleClick}>
        {isSelected ? (
          <RadioButtonCheckedIcon className="splits-select-member-radio selected" />
        ) : (
          <RadioButtonUncheckedIcon className="splits-select-member-radio" />
        )}
        {!isNewParticipant ? (
          <LetterAvatar
            size={40}
            name={participant.name}
          />
        ) : (
          <LetterAvatar
            icon={<AddIcon />}
            size={40}
            bgColor={COLORS.BACKGROUND_LIGHT}
          />
        )}

        <Typography className="splits-select-member-name">
          {participant.name}
        </Typography>
      </Box>
    );
  },
);

interface GroupsSelectParticipantCardProps {
  groupId?: string | null;
  inviterName: string;
  participants: IGroupParticipant[];
  selectedParticipant: IGroupParticipant | null;
  onSelectParticipant: (participant: IGroupParticipant) => void;
  onContinueFromSelectParticipant: (name?: string) => void;
}

export function GroupsSelectMemberCard({
  groupId,
  inviterName,
  participants,
  selectedParticipant,
  onSelectParticipant,
  onContinueFromSelectParticipant,
}: GroupsSelectParticipantCardProps) {
  const {t} = useTranslation();

  const [newParticipantName, setNewParticipantName] = React.useState('');

  const hasParticipants = participants && participants.length > 0;

  const newParticipantOption: IGroupParticipant = {
    splitUserId: NEW_PARTICIPANT_ID,
    name: t('groups.select_member.not_listed'),
    groupId: groupId || '',
    validated: false,
    balance: 0,
  };

  const showInputName = useMemo(() => {
    return (
      !hasParticipants ||
      selectedParticipant?.splitUserId === NEW_PARTICIPANT_ID
    );
  }, [hasParticipants, selectedParticipant]);

  const title = useMemo(() => {
    if (hasParticipants) {
      return t('groups.select_member.title');
    }
    return t('groups.select_member.enter_name');
  }, [hasParticipants, t]);

  const disableContinue = useMemo(() => {
    if (
      selectedParticipant &&
      selectedParticipant.splitUserId === NEW_PARTICIPANT_ID &&
      !newParticipantName.trim()
    ) {
      return true;
    }

    if (showInputName && !newParticipantName.trim()) {
      return true;
    }

    return false;
  }, [newParticipantName, selectedParticipant, showInputName]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewParticipantName(e.target.value);
    },
    [],
  );

  const handleOnContinue = useCallback(() => {
    if (showInputName && !newParticipantName.trim()) return;
    onContinueFromSelectParticipant(newParticipantName?.trim());
  }, [newParticipantName, onContinueFromSelectParticipant, showInputName]);

  return (
    <Box className="splits-landing-card splits-landing-card-select-member">
      <Box
        component="div"
        className="splits-landing-card-fdic">
        <FdicIcon />
      </Box>
      <Box className="splits-landing-card-select-member-body">
        <InviterAvatar inviterName={inviterName} />
        <Typography
          className="splits-landing-headline"
          component="h1">
          {title}
        </Typography>
        <>
          {hasParticipants && (
            <Box className="splits-select-member-list">
              {participants.map(participant => (
                <GroupsSelectMemberItem
                  key={participant.splitUserId}
                  participant={participant}
                  isSelected={
                    selectedParticipant?.splitUserId === participant.splitUserId
                  }
                  onSelect={onSelectParticipant}
                />
              ))}

              {/* I'm not listed */}
              <GroupsSelectMemberItem
                participant={newParticipantOption}
                isSelected={
                  selectedParticipant?.splitUserId === NEW_PARTICIPANT_ID
                }
                onSelect={onSelectParticipant}
                isNewParticipant
              />
            </Box>
          )}

          {showInputName && (
            <InputTextField
              id="splits-select-member-name"
              data-testid="splits-select-member-name"
              label={t('groups.select_member.input_name')}
              value={newParticipantName}
              onChange={handleOnChange}
            />
          )}

          <AppButton
            id="splits-select-member-continue"
            data-testid="splits-select-member-continue"
            onClick={handleOnContinue}
            label={t('groups.select_member.continue')}
            variant={AppButtonVariant.onPrimary}
            disabled={disableContinue}
          />
        </>
      </Box>
    </Box>
  );
}
